import {useEffect, useState} from 'react'

/**
 * Tiny SPA router that replaces `next/navigation`'s `useRouter()` /
 * `usePathname()` / `useSearchParams()`. Keeps state in `window.history`
 * so the back button works, and broadcasts location changes via the
 * `wechat-desk:locationchange` custom event.
 */

type LocationSnapshot = {
    pathname: string
    search: string
}

function readLocation(): LocationSnapshot {
    if (typeof window === 'undefined') {
        return {pathname: '/', search: ''}
    }
    return {
        pathname: window.location.pathname || '/',
        search: window.location.search || '',
    }
}

function emit() {
    window.dispatchEvent(new CustomEvent('wechat-desk:locationchange'))
}

export function push(path: string) {
    const current = window.location.pathname + window.location.search
    if (path === current) return
    window.history.pushState(null, '', path)
    emit()
}

export function replace(path: string) {
    window.history.replaceState(null, '', path)
    emit()
}

export function usePathname(): string {
    const [location, setLocation] = useState(readLocation)
    useEffect(() => {
        const handler = () => setLocation(readLocation())
        window.addEventListener('popstate', handler)
        window.addEventListener('wechat-desk:locationchange', handler)
        return () => {
            window.removeEventListener('popstate', handler)
            window.removeEventListener('wechat-desk:locationchange', handler)
        }
    }, [])
    return location.pathname
}

export function useSearchParams(): URLSearchParams {
    const [location, setLocation] = useState(readLocation)
    useEffect(() => {
        const handler = () => setLocation(readLocation())
        window.addEventListener('popstate', handler)
        window.addEventListener('wechat-desk:locationchange', handler)
        return () => {
            window.removeEventListener('popstate', handler)
            window.removeEventListener('wechat-desk:locationchange', handler)
        }
    }, [])
    return new URLSearchParams(location.search)
}

export function useRouter(): { push: typeof push; replace: typeof replace } {
    return {push, replace}
}
